import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Megaphone, Send, Building2, CheckCircle2, Search, Globe, Target,
  Bell, Clock, RefreshCw, AlertTriangle, Users
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const AdminBroadcast = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<"all" | "selected">("all");
  const [selectedNurseries, setSelectedNurseries] = useState<Set<string>>(new Set());
  const [nurserySearch, setNurserySearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Queries ──
  const { data: nurseries } = useQuery({
    queryKey: ["admin-broadcast-nurseries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("id, name, phone, address")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: recentNotifications, refetch: refetchHistory } = useQuery({
    queryKey: ["admin-broadcast-history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, body, created_at, nursery_id, nurseries(name)")
        .eq("type", "broadcast")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
  });

  const nurseryCount = nurseries?.length || 0;

  // Filter nurseries for selection
  const filteredNurseries = useMemo(() => {
    if (!nurseries) return [];
    if (!nurserySearch) return nurseries;
    return nurseries.filter((n) =>
      n.name.toLowerCase().includes(nurserySearch.toLowerCase()) ||
      n.address?.toLowerCase().includes(nurserySearch.toLowerCase())
    );
  }, [nurseries, nurserySearch]);

  // Group history by title+created_at (approximate grouping)
  const groupedHistory = useMemo(() => {
    if (!recentNotifications) return [];
    const groups: Record<string, { title: string; body: string; created_at: string; count: number; nurseries: string[] }> = {};
    recentNotifications.forEach((n: any) => {
      // Group by title + minute
      const minuteKey = n.title + "|" + n.created_at?.slice(0, 16);
      if (!groups[minuteKey]) {
        groups[minuteKey] = { title: n.title, body: n.body, created_at: n.created_at, count: 0, nurseries: [] };
      }
      groups[minuteKey].count++;
      if (n.nurseries?.name) groups[minuteKey].nurseries.push(n.nurseries.name);
    });
    return Object.values(groups).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [recentNotifications]);

  // ── Mutation ──
  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload: any = { title, body, target };
      if (target === "selected") {
        payload.nursery_ids = Array.from(selectedNurseries);
      }
      const { data, error } = await supabase.functions.invoke("send-broadcast-notification", {
        body: payload,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`تم إرسال الإشعار بنجاح إلى ${data.sent} حضانة ✅`);
      setTitle("");
      setBody("");
      setSelectedNurseries(new Set());
      setConfirmOpen(false);
      refetchHistory();
    },
    onError: (err: any) => toast.error(err.message || "فشل إرسال الإشعار"),
  });

  const recipientCount = target === "all" ? nurseryCount : selectedNurseries.size;
  const canSend = title.trim().length >= 3 && body.trim().length >= 5 && recipientCount > 0;

  const toggleNursery = (id: string) => {
    setSelectedNurseries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!nurseries) return;
    if (selectedNurseries.size === nurseries.length) {
      setSelectedNurseries(new Set());
    } else {
      setSelectedNurseries(new Set(nurseries.map((n) => n.id)));
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-amber-500" />
          إرسال إشعار عام
        </h1>
        <p className="text-muted-foreground text-sm mt-1">إرسال إشعارات لمالكي الحضانات — جميعهم أو حضانات محددة</p>
      </div>

      <Tabs defaultValue="send" className="space-y-4">
        <TabsList>
          <TabsTrigger value="send" className="gap-1"><Send className="w-4 h-4" /> إرسال إشعار</TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><Clock className="w-4 h-4" /> سجل الإشعارات</TabsTrigger>
        </TabsList>

        {/* ── Send Tab ── */}
        <TabsContent value="send">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bell className="w-5 h-5 text-primary" />
                  محتوى الإشعار
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label>عنوان الإشعار *</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: تحديث جديد على المنصة" maxLength={100} />
                  <p className="text-xs text-muted-foreground mt-1">{title.length}/100</p>
                </div>
                <div>
                  <Label>نص الإشعار *</Label>
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="اكتب محتوى الإشعار هنا..." rows={5} maxLength={500} />
                  <p className="text-xs text-muted-foreground mt-1">{body.length}/500</p>
                </div>

                {/* Target Selection */}
                <div className="space-y-3">
                  <Label>المستلمون</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={target === "all" ? "default" : "outline"}
                      className="flex-1 gap-2"
                      onClick={() => setTarget("all")}
                    >
                      <Globe className="w-4 h-4" />
                      جميع الحضانات ({nurseryCount})
                    </Button>
                    <Button
                      type="button"
                      variant={target === "selected" ? "default" : "outline"}
                      className="flex-1 gap-2"
                      onClick={() => setTarget("selected")}
                    >
                      <Target className="w-4 h-4" />
                      حضانات محددة {selectedNurseries.size > 0 && `(${selectedNurseries.size})`}
                    </Button>
                  </div>
                </div>

                {/* Nursery Selection */}
                {target === "selected" && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">اختر الحضانات ({selectedNurseries.size} محددة)</Label>
                      <Button variant="ghost" size="sm" className="text-xs h-7" onClick={toggleAll}>
                        {selectedNurseries.size === nurseries?.length ? "إلغاء الكل" : "تحديد الكل"}
                      </Button>
                    </div>
                    <div className="relative">
                      <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="بحث عن حضانة..." value={nurserySearch} onChange={(e) => setNurserySearch(e.target.value)} className="pr-9" />
                    </div>
                    <ScrollArea className="h-48">
                      <div className="space-y-1">
                        {filteredNurseries.map((n) => (
                          <label key={n.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${selectedNurseries.has(n.id) ? "bg-primary/5" : ""}`}>
                            <Checkbox
                              checked={selectedNurseries.has(n.id)}
                              onCheckedChange={() => toggleNursery(n.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{n.name}</p>
                              {n.address && <p className="text-[10px] text-muted-foreground truncate">{n.address}</p>}
                            </div>
                          </label>
                        ))}
                        {!filteredNurseries.length && (
                          <p className="text-center py-4 text-sm text-muted-foreground">لا توجد حضانات مطابقة</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={!canSend || sendMutation.isPending}
                  className="w-full gap-2"
                  size="lg"
                >
                  {sendMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      إرسال الإشعار إلى {recipientCount} حضانة
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Sidebar Info */}
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6 text-center space-y-3">
                  <Building2 className="w-10 h-10 mx-auto text-primary" />
                  <div>
                    <p className="text-3xl font-bold">{nurseryCount}</p>
                    <p className="text-sm text-muted-foreground">حضانة مسجلة</p>
                  </div>
                  <Badge variant={target === "all" ? "default" : "secondary"} className="text-xs">
                    {target === "all" ? "سيصل للجميع" : `${selectedNurseries.size} حضانة محددة`}
                  </Badge>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 text-center space-y-3">
                  <Bell className="w-10 h-10 mx-auto text-amber-500" />
                  <div>
                    <p className="text-3xl font-bold">{groupedHistory.length}</p>
                    <p className="text-sm text-muted-foreground">إشعار سابق</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">نصائح</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "اجعل العنوان واضحاً ومختصراً",
                    "اذكر التفاصيل المهمة في نص الإشعار",
                    "استخدم التحديد عند الحاجة لإبلاغ حضانات معينة",
                    "الإشعار يظهر في جرس الإشعارات لكل مالك",
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                      <p>{tip}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ── History Tab ── */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  سجل الإشعارات المرسلة
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => refetchHistory()}>
                  <RefreshCw className="w-4 h-4 ml-1" />
                  تحديث
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!groupedHistory.length ? (
                <p className="text-center py-12 text-muted-foreground">لا توجد إشعارات سابقة</p>
              ) : (
                <div className="space-y-3">
                  {groupedHistory.map((g, i) => (
                    <div key={i} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-sm">{g.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{g.body}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 mr-3">
                          <Users className="w-3 h-3 ml-1" />
                          {g.count} حضانة
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{format(new Date(g.created_at), "dd MMMM yyyy - HH:mm", { locale: ar })}</span>
                        {g.nurseries.length <= 3 ? (
                          <span>{g.nurseries.join("، ")}</span>
                        ) : (
                          <span>{g.nurseries.slice(0, 2).join("، ")} و{g.nurseries.length - 2} أخرى</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              تأكيد إرسال الإشعار
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-3 space-y-2 text-sm">
              <p><span className="font-medium">العنوان:</span> {title}</p>
              <p><span className="font-medium">المحتوى:</span> {body}</p>
              <p><span className="font-medium">المستلمون:</span> {target === "all" ? `جميع الحضانات (${nurseryCount})` : `${selectedNurseries.size} حضانة محددة`}</p>
            </div>
            {target === "selected" && selectedNurseries.size <= 5 && nurseries && (
              <div className="text-xs text-muted-foreground">
                <p className="font-medium mb-1">الحضانات المحددة:</p>
                {nurseries.filter((n) => selectedNurseries.has(n.id)).map((n) => (
                  <span key={n.id} className="inline-block bg-muted px-2 py-0.5 rounded ml-1 mb-1">{n.name}</span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmOpen(false)}>إلغاء</Button>
              <Button className="flex-1 gap-2" disabled={sendMutation.isPending} onClick={() => sendMutation.mutate()}>
                {sendMutation.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendMutation.isPending ? "جاري الإرسال..." : "تأكيد الإرسال"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBroadcast;
